import { type BaseQueryApi, createApi, type FetchArgs } from "@reduxjs/toolkit/query/react";

import { fetchWithAuth } from "~/common/fetch";
import type { TPatient } from "~/types/patient";

export type TYooKassaPaymentStatus = "pending" | "succeeded" | "canceled";

export const api = createApi({
  reducerPath: "api",
  baseQuery: fetchWithAuthBaseQuery,
  refetchOnMountOrArgChange: true,
  tagTypes: ["Event", "MeetingFeedback", "Toast", "Agreement", "Meeting", "ChatThread", "ChatMessage"],
  endpoints: (builder) => ({
    getYear: builder.query<{ year: number }, void>({
      query: () => ({ url: "year" }),
    }),
    getInvoicePaymentStatus: builder.query<
      { status: TYooKassaPaymentStatus },
      { patientId: TPatient["patient_id"]; invoiceId: number; patientToken?: string }
    >({
      query: (params) => {
        const args: { token?: string; patient_id?: number } = {};

        if (typeof params.patientToken == "string") {
          args.token = params.patientToken;
          args.patient_id = params.patientId;
        }

        return {
          url: `doctor/patients/${params.patientId}/invoice/${params.invoiceId}`,
          params: args,
        };
      },
    }),
    getInvoicePaymentBatchStatus: builder.query<
      { status: TYooKassaPaymentStatus },
      { batchId: string }
    >({
      query: (params) => ({ url: `doctor/payments/invoices/batch/${params.batchId}` }),
    }),
    getAdditionalAlignersPatients: builder.query<
      { id: TPatient["patient_id"]; name: string; steps: number }[],
      void
    >({
      query: () => ({ url: "doctor/patients/additional-aligners" }),
    }),

    getChatThreads: builder.query<
      {
        threads: {
          id: number;
          channel: "express_support" | "personal_technician" | "personal_manager" | "care_department";
          unread_count: number;
          assignee?: null | {
            account_id: number;
            first_name: string;
            last_name: string;
            middle_name: string;
            avatar: string | null;
          };
          last_message: null | {
            id: number;
            sender_type: "doctor" | "lab";
            text: string;
            created_at: string | null;
          };
        }[];
      },
      void
    >({
      query: () => ({ url: "chat/threads" }),
      providesTags: ["ChatThread"],
    }),

    getChatMessages: builder.query<
      {
        messages: {
          id: number;
          sender_type: "doctor" | "lab";
          text: string;
          created_at: string | null;
          sender_account_id?: number;
          sender_avatar?: string | null;
          sender_first_name?: string;
          sender_last_name?: string;
          sender_middle_name?: string;
          files: { id: number; file_name: string; mime_type: string | null; get_url: string | null }[];
        }[];
      },
      { threadId: number; beforeId?: number; limit?: number }
    >({
      query: ({ threadId, beforeId, limit }) => ({
        url: `chat/threads/${threadId}/messages`,
        params: {
          ...(typeof beforeId == "number" ? { before_id: String(beforeId) } : {}),
          ...(typeof limit == "number" ? { limit: String(limit) } : {}),
        },
      }),
      providesTags: (_res, _err, arg) => [{ type: "ChatMessage", id: arg.threadId }],
    }),

    sendChatMessage: builder.mutation<
      { message: unknown },
      { threadId: number; text?: string; fileIds?: number[] }
    >({
      query: ({ threadId, text, fileIds }) => ({
        url: `chat/threads/${threadId}/messages/send`,
        method: "POST",
        body: { text, file_ids: fileIds ?? [] },
      }),
      invalidatesTags: (_res, _err, arg) => [
        "ChatThread",
        { type: "ChatMessage", id: arg.threadId },
      ],
    }),

    markChatRead: builder.mutation<{ status: "success" }, { threadId: number; lastMessageId: number }>({
      query: ({ threadId, lastMessageId }) => ({
        url: `chat/threads/${threadId}/read`,
        method: "POST",
        body: { last_message_id: lastMessageId },
      }),
      invalidatesTags: ["ChatThread"],
    }),

    getChatMedia: builder.query<
      {
        files: { id: number; file_name: string; mime_type: string | null; get_url: string | null; created_at: string }[];
      },
      { threadId: number }
    >({
      query: ({ threadId }) => ({ url: `chat/threads/${threadId}/media` }),
    }),

    // NOTE: Chat uploads are multipart, handled outside RTK JSON baseQuery.
  }),
});

async function fetchWithAuthBaseQuery(args: string | FetchArgs, api: BaseQueryApi) {
  try {
    const fetchArgs = typeof args == "string" ? { url: args } : args;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const { url, method = "GET", body, headers, params } = fetchArgs;
    const searchParams = new URLSearchParams(params);

    const response = await fetchWithAuth(
      `${process.env.API}/${url}` + (searchParams.size > 0 ? `?${searchParams.toString()}` : ""),
      {
        method,
        body: body ? JSON.stringify(body) : undefined,
        headers: headers as HeadersInit,
        signal: api.signal,
      },
    );

    const data = await response.json();

    if (!response.ok) {
      return {
        error: {
          status: response.status,
          data,
        },
      };
    }

    return { data };
  } catch (err) {
    return {
      error: {
        status: "FETCH_ERROR",
        data: (err as Error).message,
      },
    };
  }
}

export const {
  useGetInvoicePaymentStatusQuery,
  useGetInvoicePaymentBatchStatusQuery,
  useGetAdditionalAlignersPatientsQuery,
  useGetChatThreadsQuery,
  useGetChatMessagesQuery,
  useSendChatMessageMutation,
  useMarkChatReadMutation,
  useGetChatMediaQuery,
} = api;
